import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { authApi, useMe } from "@/shared/api/auth";
import { errorMessage } from "@/shared/api/client";
import { Button } from "@/shared/ui/button";
import { AuthLayout, AuthTitle, CodeStep, EmailField } from "@/core/auth-flow";
import { isEmail, safeNext } from "@/core/auth-utils";
import { projectPath } from "@/core/projects-ui";

/** Belépés: e-mail → kód. Ismeretlen címnél **nem** vált át magától regisztrációra,
 *  hanem felkínálja (rendszerterv 6.). */
export function LoginPage() {
  const { data: me, isPending } = useMe();
  const [params] = useSearchParams();
  const project = params.get("project");
  const next = safeNext(params.get("next")) || projectPath(project);

  const [email, setEmail] = useState("");
  const [devCode, setDevCode] = useState<string | null>(null);
  const [step, setStep] = useState<"email" | "code">("email");
  const [unknown, setUnknown] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const request = useMutation({
    mutationFn: (e: string) => authApi.requestCode(e, project ?? undefined),
    onSuccess: (r) => {
      if (r.status === "code_sent") {
        setDevCode(r.dev_code ?? null);
        setStep("code");
      } else {
        setUnknown(true);
      }
    },
    onError: (e) => setError(errorMessage(e)),
  });

  if (!isPending && me) return <Navigate to={next} replace />;

  const registerHref = `/app/regisztracio?${new URLSearchParams({
    ...(project ? { project } : {}),
    ...(email ? { email } : {}),
  })}`;

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    setUnknown(false);
    const v = email.trim();
    if (!isEmail(v)) return setError("Adj meg egy érvényes e-mail címet.");
    request.mutate(v);
  };

  return (
    <AuthLayout>
      {step === "code" ? (
        <CodeStep
          email={email.trim()}
          next={next}
          devCode={devCode}
          project={project ?? undefined}
          onBack={() => setStep("email")}
        />
      ) : (
        <form onSubmit={submit} noValidate className="flex flex-col gap-5">
          <AuthTitle title="Belépés">
            Jelszó nincs: az e-mail címedre küldünk egy hatjegyű kódot.
          </AuthTitle>
          <EmailField value={email} onChange={setEmail} error={error} />
          {unknown && (
            <div className="flex flex-col gap-2 rounded-lg border border-line bg-gray px-4 py-3">
              <p className="text-[14px] text-ink-soft">
                Ehhez a címhez még nincs fiók. Regisztrálj — pár másodperc.
              </p>
              <Link to={registerHref} className="text-[14px] font-semibold text-gold-text">
                Regisztrálok →
              </Link>
            </div>
          )}
          <Button type="submit" size="lg" block disabled={request.isPending}>
            {request.isPending ? "Küldés…" : "Kódot kérek"}
          </Button>
          <p className="text-[15px] text-muted">
            Még nincs fiókod?{" "}
            <Link to={registerHref} className="font-semibold text-gold-text">
              Regisztrálok
            </Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
